import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  Optional,
  output,
  signal,
} from '@angular/core';
import { TranslocoDirective } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  InputTextComponent,
  InputRadioGroupComponent,
} from '@pedix-workspace/angular-ui-form-reactive';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { AddressComponent } from '../../address/address.component';
import {
  Address,
  CartItem,
  Country,
  EstablishmentCheckout,
  ShippingEstimate,
  ShippingOption,
  TypesOfShippings,
} from '@pedix-workspace/utils';
import {
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ShippingCostResponse } from '@pedix-workspace/shared-delivery';
import { DeliveryService } from '@pedix-workspace/pedixapp-core-services';
import { ApiV1_AvailableIntegration, ApiV1_Establishment } from '@pedix-workspace/api-v1';

@UntilDestroy()
@Component({
  selector: 'pxw-end-order-shipping-option-fields',
  templateUrl: './end-order-shipping-option-fields.component.html',
  styleUrls: ['./end-order-shipping-option-fields.component.scss'],
  providers: [],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    InputTextComponent,
    FormsModule,
    InputRadioGroupComponent,
    AddressComponent,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      deps: [[Optional, NgForm]],
      useFactory: (ngForm: NgForm) => ngForm,
    },
  ],
})
export class EndOrderShippingOptionFieldsComponent {
  private readonly deliveryService = inject(DeliveryService);

  readonly workerServicesUrl = input.required<string>();
  readonly countryCodes = input.required<Country[] | null>();
  readonly apiUrl = input.required<string>();
  readonly checkoutData = input.required<EstablishmentCheckout>();
  readonly establishment = input.required<ApiV1_Establishment>();
  readonly totalAmountWithDiscount = input.required<number>();
  readonly cartItems = input.required<CartItem[]>();
  readonly availableLogisticIntegrations = input.required<ApiV1_AvailableIntegration[]>();
  readonly name = input.required<string | null>();
  readonly phone = input.required<string | null>();

  readonly isFormSubmitted = input<boolean>(false);
  readonly previousShippingEstimate = input<ShippingEstimate | null>(null);

  readonly shippingOptionType = model.required<TypesOfShippings | null>();
  readonly address = model.required<Address | null>();
  readonly tableNumber = model.required<string | null>();
  readonly shippingCostResponse = model<ShippingCostResponse | null>(null);
  readonly calculatingShippingCost = model<boolean>(false);

  readonly isFirstTimeCalculatingShippingCost = signal(true);

  readonly selectedShippingOptionChange = output<ShippingOption | null>();

  // NOTE: we merge together the DELIVERY and PARCEL types in the shippingOptionTypes selector
  readonly shippingOptionTypes = computed(() => {
    const shippingOptions: TypesOfShippings[] = [];

    if (this.checkoutData().shippingOptionInPlace?.active) {
      shippingOptions.push(TypesOfShippings.IN_PLACE);
    }
    if (this.checkoutData().shippingOptionTakeAway?.active) {
      shippingOptions.push(TypesOfShippings.TAKE_AWAY);
    }
    if (
      this.checkoutData().shippingOptionDelivery?.active ||
      this.checkoutData().shippingOptionParcel?.active
    ) {
      shippingOptions.push(TypesOfShippings.DELIVERY);
    }

    return shippingOptions;
  });
  // NOTE: because we merged together the DELIVERY and PARCEL types in the shippingOption selector
  // we need a way to get the correct shippingOption, which in this case will be received as part of the shippingCostResponse
  // otherwhise, get the shippingOption from the checkoutData configuration
  readonly selectedShippingOption = computed(() => {
    if (!this.shippingOptionType()) {
      return null;
    }
    if (this.shippingOptionType() === TypesOfShippings.IN_PLACE) {
      return this.checkoutData().shippingOptionInPlace!;
    }
    if (this.shippingOptionType() === TypesOfShippings.TAKE_AWAY) {
      return this.checkoutData().shippingOptionTakeAway!;
    }
    return (
      this.shippingCostResponse()?.shippingOption ||
      this.checkoutData()?.shippingOptionDelivery ||
      null
    );
  });

  protected typesOfShippings = TypesOfShippings;

  constructor() {
    effect(() => {
      this.selectedShippingOptionChange.emit(this.selectedShippingOption());
    });
    effect(() => {
      if (!this.shippingOptionType()) {
        return null;
      }
      if (this.shippingOptionType() === TypesOfShippings.IN_PLACE) {
        return this.checkoutData().shippingOptionInPlace;
      }
      if (this.shippingOptionType() === TypesOfShippings.TAKE_AWAY) {
        return this.checkoutData().shippingOptionTakeAway;
      }
      return (
        this.shippingCostResponse()?.shippingOption || this.checkoutData()?.shippingOptionDelivery
      );
    });
    // Calculate shipping cost every time shippingOption or addressDetails changes
    combineLatest([
      toObservable(this.shippingOptionType),
      toObservable(this.address).pipe(
        map(address => address || {}),
        distinctUntilKeyChanged('addressDetails'),
      ),
    ])
      .pipe(
        filter(([shippingOptionType]) => !!shippingOptionType),
        tap(() => this.calculatingShippingCost.set(true)),
        switchMap(([shippingOptionType]) => {
          if (
            shippingOptionType !== TypesOfShippings.DELIVERY &&
            shippingOptionType !== TypesOfShippings.PARCEL
          ) {
            return of(null);
          }
          return from(
            this.deliveryService.calculateShippingCost({
              apiBaseUrl: this.apiUrl(),
              shippingOptionDelivery: this.checkoutData()?.shippingOptionDelivery,
              shippingOptionParcel: this.checkoutData()?.shippingOptionParcel,
              address: this.address(),
              name: this.name(),
              phone: this.phone(),
              hasLogisticsIntegration: this.availableLogisticIntegrations().length > 0,
              establishment: this.establishment(),
              cartItems: this.cartItems(),
              totalAmountWithDiscount: this.totalAmountWithDiscount(),
              previousShippingEstimate: this.isFirstTimeCalculatingShippingCost()
                ? this.previousShippingEstimate()
                : null,
            }),
          );
        }),
        untilDestroyed(this),
      )
      .subscribe(shippingCostResponse => {
        this.shippingCostResponse.set(shippingCostResponse);
        this.isFirstTimeCalculatingShippingCost.set(false);

        this.calculatingShippingCost.set(false);
      });
  }
}
